import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Typography } from '@lyearn/ui';
import TagsAndProperties from '@/components/FilterManager/Tags/TagsAndProperties';
import { SystemProperty } from '@/components/FilterManager/Tags/TagsAndProperties/types';
import Loader from '@/components/Form/components/Conditions/components/Loader';
import { GroupTypeEnum, ReviewerConfigInput, ReviewerTypeEnum, TagTypeEnum } from '@/types/schema';

import { validateReviewerTagError } from '../utils';

const ALLOWED_TAG_TYPES = [TagTypeEnum.User];
const ALLOWED_PROPERTIES = [SystemProperty.ManagerId];

const getSelectedValue = (value: string) => {
  if (ALLOWED_PROPERTIES.includes(value as SystemProperty)) {
    return { fieldName: value, tagId: undefined };
  }

  return { fieldName: undefined, tagId: value };
};

function SelectTag({
  reviewerConfig,
  showError,
  type,
  onReviewerConfigChange,
  className,
}: {
  reviewerConfig: ReviewerConfigInput;
  showError?: boolean;
  type: ReviewerTypeEnum;
  onReviewerConfigChange: (nextValue: ReviewerConfigInput) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const onChange = useCallback(
    ({ value }: any) => onReviewerConfigChange({ ...reviewerConfig, ...getSelectedValue(value) }),
    [onReviewerConfigChange, reviewerConfig],
  );

  const error = Boolean(showError && validateReviewerTagError(reviewerConfig));

  if (type !== ReviewerTypeEnum.Dynamic) {
    return null;
  }

  return (
    <Box className={cn('space-y-8', className)}>
      <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
        {t('Exercise:Creator.BasedOnTag')}
      </Typography>
      <TagsAndProperties
        Loader={<Loader className="h-48" />}
        allowedProperties={ALLOWED_PROPERTIES}
        clearOnBlur={false}
        error={error}
        placeholder={t('Exercise:Creator.SelectTag')}
        selectedProperty={reviewerConfig.fieldName ?? reviewerConfig.tagId}
        size="large"
        tagTypes={ALLOWED_TAG_TYPES}
        type={GroupTypeEnum.UserCollection}
        onChange={onChange}
      />
      {error ? (
        <Typography className="ms-8" color="text-text-error" variant="body-short-03">
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export default SelectTag;
